import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Plus, ShoppingCart, Eye, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PurchaseOrders = () => {
  const { getAuthHeader } = useAuth();
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [viewPO, setViewPO] = useState(null);
  const [formData, setFormData] = useState({
    project_id: '',
    vendor_id: '',
    items: [],
    delivery_date: '',
    notes: ''
  });
  const [newItem, setNewItem] = useState({ material_id: '', quantity: 0, unit_price: 0 });

  const fetchData = useCallback(async () => {
    try {
      const [posRes, vendorsRes, materialsRes, projectsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/erp/procurement/purchase-orders`, { headers: getAuthHeader() }),
        axios.get(`${BACKEND_URL}/api/erp/procurement/vendors`, { headers: getAuthHeader() }),
        axios.get(`${BACKEND_URL}/api/erp/procurement/materials`, { headers: getAuthHeader() }),
        axios.get(`${BACKEND_URL}/api/erp/projects`, { headers: getAuthHeader() })
      ]);
      setPurchaseOrders(posRes.data);
      setVendors(vendorsRes.data);
      setMaterials(materialsRes.data);
      setProjects(projectsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const addItem = () => {
    if (newItem.material_id && newItem.quantity > 0 && newItem.unit_price > 0) {
      setFormData({
        ...formData,
        items: [...formData.items, {...newItem}]
      });
      setNewItem({ material_id: '', quantity: 0, unit_price: 0 });
    }
  };

  const removeItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${BACKEND_URL}/api/erp/procurement/purchase-orders`,
        formData,
        { headers: getAuthHeader() }
      );
      fetchData();
      setShowModal(false);
      setFormData({ project_id: '', vendor_id: '', items: [], delivery_date: '', notes: '' });
    } catch (error) {
      console.error('Error creating PO:', error);
    }
  };

  const updateStatus = async (poId, status) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/erp/procurement/purchase-orders/${poId}/status?status=${status}`,
        {},
        { headers: getAuthHeader() }
      );
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getVendorName = (id) => vendors.find(v => v.id === id)?.name || 'Unknown';
  const getMaterialName = (id) => materials.find(m => m.id === id)?.name || 'Unknown';

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Purchase Orders</h1>
          <p className="text-gray-600">Manage procurement and orders</p>
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-primary">
          <Plus className="w-4 h-4 mr-2" />
          Create PO
        </Button>
      </div>

      <div className="grid gap-4">
        {purchaseOrders.map(po => (
          <Card key={po.id} className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-bold text-lg">{po.po_number}</h3>
                  <span className={`text-xs px-2 py-1 rounded ${
                    po.status === 'delivered' ? 'bg-green-100 text-green-700' :
                    po.status === 'pending' ? 'bg-orange-100 text-orange-700' :
                    po.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                    'bg-blue-100 text-blue-700'
                  }`}>
                    {po.status}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Vendor:</span>
                    <p className="font-medium">{getVendorName(po.vendor_id)}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Total Amount:</span>
                    <p className="font-bold text-green-600">₹{(po.total_amount / 100000).toFixed(2)}L</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Delivery Date:</span>
                    <p className="font-medium">{new Date(po.delivery_date).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Items:</span>
                    <p className="font-medium">{po.items?.length || 0} items</p>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                {po.status === 'pending' && (
                  <Button size="sm" onClick={() => updateStatus(po.id, 'delivered')} className="bg-green-600">
                    Mark Delivered
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setViewPO(po)}>
                  <Eye size={14} className="mr-1" />
                  View
                </Button>
              </div>
            </div>
          </Card>
        ))}
        {purchaseOrders.length === 0 && (
          <Card className="p-12 text-center">
            <ShoppingCart size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">No purchase orders yet</p>
          </Card>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Create Purchase Order</h2>
              <Button size="sm" variant="ghost" onClick={() => setShowModal(false)}>
                <X size={20} />
              </Button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Vendor</label>
                  <select
                    value={formData.vendor_id}
                    onChange={(e) => setFormData({...formData, vendor_id: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  >
                    <option value="">Select Vendor</option>
                    {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Project (Optional)</label>
                  <select
                    value={formData.project_id}
                    onChange={(e) => setFormData({...formData, project_id: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="">Select Project</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.name || p.project_name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Delivery Date</label>
                <input
                  type="date"
                  value={formData.delivery_date}
                  onChange={(e) => setFormData({...formData, delivery_date: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Items</label>
                <div className="border rounded-lg p-4 mb-2">
                  <div className="grid grid-cols-4 gap-2 mb-2">
                    <select
                      value={newItem.material_id}
                      onChange={(e) => setNewItem({...newItem, material_id: e.target.value})}
                      className="col-span-2 px-3 py-2 border rounded-lg"
                    >
                      <option value="">Select Material</option>
                      {materials.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                    <input
                      type="number"
                      placeholder="Quantity"
                      value={newItem.quantity}
                      onChange={(e) => setNewItem({...newItem, quantity: parseFloat(e.target.value)})}
                      className="px-3 py-2 border rounded-lg"
                    />
                    <input
                      type="number"
                      placeholder="Price"
                      value={newItem.unit_price}
                      onChange={(e) => setNewItem({...newItem, unit_price: parseFloat(e.target.value)})}
                      className="px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <Button type="button" size="sm" onClick={addItem}>Add Item</Button>
                </div>
                {formData.items.length > 0 && (
                  <div className="space-y-2">
                    {formData.items.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span>{getMaterialName(item.material_id)} - Qty: {item.quantity} - ₹{item.unit_price}</span>
                        <Button type="button" size="sm" variant="ghost" onClick={() => removeItem(idx)}>
                          <X size={14} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="2"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button type="submit" className="bg-primary">Create Purchase Order</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {viewPO && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="p-6 max-w-2xl w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">PO Details - {viewPO.po_number}</h2>
              <Button size="sm" variant="ghost" onClick={() => setViewPO(null)}>
                <X size={20} />
              </Button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-gray-600">Vendor:</span>
                  <p className="font-medium">{getVendorName(viewPO.vendor_id)}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Status:</span>
                  <p className="font-medium">{viewPO.status}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Delivery Date:</span>
                  <p className="font-medium">{new Date(viewPO.delivery_date).toLocaleDateString()}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Total Amount:</span>
                  <p className="font-bold text-green-600">₹{(viewPO.total_amount / 100000).toFixed(2)}L</p>
                </div>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Items:</h3>
                <div className="space-y-2">
                  {viewPO.items?.map((item, idx) => (
                    <div key={idx} className="p-3 bg-gray-50 rounded">
                      <div className="flex justify-between">
                        <span className="font-medium">{getMaterialName(item.material_id)}</span>
                        <span>Qty: {item.quantity} × ₹{item.unit_price} = ₹{(item.quantity * item.unit_price).toLocaleString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {viewPO.notes && (
                <div>
                  <span className="text-sm text-gray-600">Notes:</span>
                  <p>{viewPO.notes}</p>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrders;